import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtNum, fmtDate, today } from "@/lib/format";
import { Kpi, Panel, Badge2, Bar, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIERS = [
  ["L1", "L1 Master Plan", "Yearly milestones & overall scope"],
  ["L2", "L2 Target Plan", "Monthly look-ahead schedule"],
  ["L3", "L3 Execution Plan", "Weekly work plan (WWP)"],
];

export default function Planning() {
  const { pid } = useParams();
  const [tier, setTier] = useState("L2");
  const [rows, setRows] = useState([]);
  const [acts, setActs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ activity_id: "", label: "", period_start: today(), period_end: today(), target_qty: "", owner: "Planning Cell" });

  const load = () => {
    api.get("/plans", { params: { project_id: pid, tier } }).then(({ data }) => setRows(data));
    api.get(`/projects/${pid}/wbs`).then(({ data }) => setActs(data.filter((a) => a.total_qty > 0)));
  };
  useEffect(() => { load(); }, [pid, tier]);

  const save = async () => {
    const act = acts.find((a) => a.id === form.activity_id);
    try {
      await api.post("/plans", {
        project_id: pid, tier, activity_id: form.activity_id || null,
        label: form.label || `${tier} target — ${act?.name || "Project"}`,
        period_start: form.period_start, period_end: form.period_end,
        target_qty: Number(form.target_qty), unit: act?.unit || "", owner: form.owner,
      });
      toast.success("Plan target created");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const attain = rows.length ? rows.reduce((s, r) => s + r.attainment_pct, 0) / rows.length : 0;

  return (
    <div data-testid="planning-page">
      <PageHead title="Multi-Tier Integrated Planning" subtitle="L1 yearly → L2 monthly → L3 weekly. Achievement auto-synced from L4 daily logs.">
        <Button onClick={() => setOpen(true)} data-testid="plan-new-btn"><Plus className="w-4 h-4 mr-1.5" /> Add {tier} target</Button>
      </PageHead>

      <Tabs value={tier} onValueChange={setTier} className="mb-3">
        <TabsList data-testid="plan-tier-tabs">
          {TIERS.map(([t, l]) => <TabsTrigger key={t} value={t} data-testid={`plan-tab-${t}`}>{l}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Kpi label="Targets in tier" value={rows.length} testId="kpi-plan-count" />
        <Kpi label="Avg attainment" value={`${fmtNum(attain, 1)}%`} tone={attain >= 90 ? "good" : attain >= 70 ? "warn" : "bad"} testId="kpi-plan-attainment" />
        <Kpi label="Targets met" value={rows.filter((r) => r.attainment_pct >= 100).length} tone="good" />
        <Kpi label="Slipping" value={rows.filter((r) => r.attainment_pct < 80).length} tone="bad" />
      </div>

      <Panel title={`${TIERS.find((t) => t[0] === tier)?.[1]} — ${TIERS.find((t) => t[0] === tier)?.[2]}`} testId="plan-table-panel">
        <div className="overflow-x-auto">
          <table className="w-full dense-table">
            <thead className="sticky-head">
              <tr><th>Plan</th><th>Activity</th><th>Period</th><th className="text-right">Target</th><th className="text-right">Achieved</th><th className="text-right">Variance</th><th>Attainment</th><th>Owner</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid={`plan-row-${p.id}`}>
                  <td className="max-w-[260px] truncate">{p.label}</td>
                  <td className="text-muted-foreground max-w-[180px] truncate">{p.activity_name}</td>
                  <td className="num text-[11px]">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</td>
                  <td className="num text-right">{fmtNum(p.target_qty, 1)} {p.unit}</td>
                  <td className="num text-right text-emerald-500">{fmtNum(p.achieved_qty, 1)}</td>
                  <td className={`num text-right ${p.variance >= 0 ? "text-emerald-500" : "text-red-500"}`}>{fmtNum(p.variance, 1)}</td>
                  <td><Bar pct={p.attainment_pct} /></td>
                  <td className="text-muted-foreground">{p.owner}</td>
                  <td>
                    <button onClick={async () => { await api.delete(`/plans/${p.id}`); load(); }} className="text-muted-foreground hover:text-red-500" data-testid={`plan-delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? <tr><td colSpan={9} className="text-muted-foreground">No {tier} targets yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New {tier} target</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Activity</Label>
              <Select value={form.activity_id} onValueChange={(v) => setForm({ ...form, activity_id: v })}>
                <SelectTrigger data-testid="plan-activity-select"><SelectValue placeholder="Select activity" /></SelectTrigger>
                <SelectContent>{acts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="plan-label-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Period start</Label>
              <Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="plan-start-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Period end</Label>
              <Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="plan-end-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Target qty</Label>
              <Input value={form.target_qty} onChange={(e) => setForm({ ...form, target_qty: e.target.value })} data-testid="plan-target-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Owner</Label>
              <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} data-testid="plan-owner-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.target_qty} data-testid="plan-save-btn">Save target</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
